import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as HRMService from '../services/HRMService';

// ==================== Employee Hooks ====================

export const useEmployees = (chatbotId, filters = {}, options = {}) =>
  useQuery({
    queryKey: ['hrm-employees', chatbotId, filters],
    queryFn: () => HRMService.getEmployees({ chatbot_id: chatbotId, ...filters }),
    select: (res) => res.data,
    enabled: !!chatbotId,
    ...options,
  });

export const useEmployee = (chatbotId, employeeId, options = {}) =>
  useQuery({
    queryKey: ['hrm-employee', chatbotId, employeeId],
    queryFn: () => HRMService.getEmployee(employeeId, { chatbot_id: chatbotId }),
    select: (res) => res.data,
    enabled: !!chatbotId && !!employeeId,
    ...options,
  });

export const useCreateEmployee = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => HRMService.createEmployee({ ...data, chatbot_id: chatbotId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hrm-employees', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['hrm-dashboard', chatbotId] });
    },
  });
};

export const useUpdateEmployee = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) => HRMService.updateEmployee(id, { ...data, chatbot_id: chatbotId }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hrm-employees', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['hrm-employee', chatbotId, variables.id] });
      queryClient.invalidateQueries({ queryKey: ['hrm-dashboard', chatbotId] });
    },
  });
};

export const useDeleteEmployee = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => HRMService.deleteEmployee(id, { chatbot_id: chatbotId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hrm-employees', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['hrm-dashboard', chatbotId] });
    },
  });
};

// ==================== Department Hooks ====================

export const useDepartments = (chatbotId, filters = {}, options = {}) =>
  useQuery({
    queryKey: ['hrm-departments', chatbotId, filters],
    queryFn: () => HRMService.getDepartments({ chatbot_id: chatbotId, ...filters }),
    select: (res) => res.data,
    enabled: !!chatbotId,
    ...options,
  });

export const useCreateDepartment = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => HRMService.createDepartment({ ...data, chatbot_id: chatbotId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hrm-departments', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['hrm-dashboard', chatbotId] });
    },
  });
};

export const useUpdateDepartment = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) => HRMService.updateDepartment(id, { ...data, chatbot_id: chatbotId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hrm-departments', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['hrm-dashboard', chatbotId] });
    },
  });
};

export const useDeleteDepartment = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => HRMService.deleteDepartment(id, { chatbot_id: chatbotId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hrm-departments', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['hrm-dashboard', chatbotId] });
    },
  });
};

// ==================== Position Hooks ====================

export const usePositions = (chatbotId, filters = {}, options = {}) =>
  useQuery({
    queryKey: ['hrm-positions', chatbotId, filters],
    queryFn: () => HRMService.getPositions({ chatbot_id: chatbotId, ...filters }),
    select: (res) => res.data,
    enabled: !!chatbotId,
    ...options,
  });

export const useCreatePosition = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => HRMService.createPosition({ ...data, chatbot_id: chatbotId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hrm-positions', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['hrm-dashboard', chatbotId] });
    },
  });
};

export const useUpdatePosition = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) => HRMService.updatePosition(id, { ...data, chatbot_id: chatbotId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hrm-positions', chatbotId] });
    },
  });
};

export const useDeletePosition = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => HRMService.deletePosition(id, { chatbot_id: chatbotId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hrm-positions', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['hrm-dashboard', chatbotId] });
    },
  });
};

// ==================== Lifecycle Hooks ====================

export const useLifecycleEvents = (chatbotId, filters = {}, options = {}) =>
  useQuery({
    queryKey: ['hrm-lifecycle', chatbotId, filters],
    queryFn: () => HRMService.getLifecycleEvents({ chatbot_id: chatbotId, ...filters }),
    select: (res) => res.data,
    enabled: !!chatbotId,
    ...options,
  });

export const useCreateLifecycleEvent = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => HRMService.createLifecycleEvent({ ...data, chatbot_id: chatbotId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hrm-lifecycle', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['hrm-employees', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['hrm-dashboard', chatbotId] });
    },
  });
};

// ==================== Dashboard Hook ====================

export const useHRMDashboard = (chatbotId, options = {}) =>
  useQuery({
    queryKey: ['hrm-dashboard', chatbotId],
    queryFn: () => HRMService.getHRMDashboard({ chatbot_id: chatbotId }),
    select: (res) => res.data,
    enabled: !!chatbotId,
    ...options,
  });

// ==================== Job Hooks ====================

export const useJobs = (chatbotId, filters = {}, options = {}) =>
  useQuery({
    queryKey: ['hrm-jobs', chatbotId, filters],
    queryFn: () => HRMService.getJobs({ chatbot_id: chatbotId, ...filters }),
    select: (res) => res.data,
    enabled: !!chatbotId,
    ...options,
  });

export const useJob = (chatbotId, jobId, options = {}) =>
  useQuery({
    queryKey: ['hrm-job', chatbotId, jobId],
    queryFn: () => HRMService.getJob(jobId, { chatbot_id: chatbotId }),
    select: (res) => res.data,
    enabled: !!chatbotId && !!jobId,
    ...options,
  });

export const useCreateJob = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => HRMService.createJob({ ...data, chatbot_id: chatbotId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hrm-jobs', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['recruitment-dashboard', chatbotId] });
    },
  });
};

export const useUpdateJob = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) => HRMService.updateJob(id, { ...data, chatbot_id: chatbotId }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hrm-jobs', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['hrm-job', chatbotId, variables.id] });
      queryClient.invalidateQueries({ queryKey: ['recruitment-dashboard', chatbotId] });
    },
  });
};

export const useDeleteJob = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => HRMService.deleteJob(id, { chatbot_id: chatbotId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hrm-jobs', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['recruitment-dashboard', chatbotId] });
    },
  });
};

// ==================== Applicant Hooks ====================

export const useApplicants = (chatbotId, filters = {}, options = {}) =>
  useQuery({
    queryKey: ['hrm-applicants', chatbotId, filters],
    queryFn: () => HRMService.getApplicants({ chatbot_id: chatbotId, ...filters }),
    select: (res) => res.data,
    enabled: !!chatbotId,
    ...options,
  });

export const useApplicant = (chatbotId, applicantId, options = {}) =>
  useQuery({
    queryKey: ['hrm-applicant', chatbotId, applicantId],
    queryFn: () => HRMService.getApplicant(applicantId, { chatbot_id: chatbotId }),
    select: (res) => res.data,
    enabled: !!chatbotId && !!applicantId,
    ...options,
  });

export const useCreateApplicant = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => HRMService.createApplicant({ ...data, chatbot_id: chatbotId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hrm-applicants', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['recruitment-dashboard', chatbotId] });
    },
  });
};

export const useUpdateApplicant = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) => HRMService.updateApplicant(id, { ...data, chatbot_id: chatbotId }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hrm-applicants', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['hrm-applicant', chatbotId, variables.id] });
      queryClient.invalidateQueries({ queryKey: ['recruitment-dashboard', chatbotId] });
    },
  });
};

export const useUpdateApplicantStatus = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }) => HRMService.updateApplicantStatus(id, { status, chatbot_id: chatbotId }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hrm-applicants', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['hrm-applicant', chatbotId, variables.id] });
      queryClient.invalidateQueries({ queryKey: ['recruitment-dashboard', chatbotId] });
    },
  });
};

// ==================== Interview Hooks ====================

export const useInterviews = (chatbotId, filters = {}, options = {}) =>
  useQuery({
    queryKey: ['hrm-interviews', chatbotId, filters],
    queryFn: () => HRMService.getInterviews({ chatbot_id: chatbotId, ...filters }),
    select: (res) => res.data,
    enabled: !!chatbotId,
    ...options,
  });

export const useCreateInterview = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => HRMService.createInterview({ ...data, chatbot_id: chatbotId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hrm-interviews', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['hrm-applicants', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['recruitment-dashboard', chatbotId] });
    },
  });
};

export const useUpdateInterview = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) => HRMService.updateInterview(id, { ...data, chatbot_id: chatbotId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hrm-interviews', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['recruitment-dashboard', chatbotId] });
    },
  });
};

// ==================== Offer Hooks ====================

export const useOffers = (chatbotId, filters = {}, options = {}) =>
  useQuery({
    queryKey: ['hrm-offers', chatbotId, filters],
    queryFn: () => HRMService.getOffers({ chatbot_id: chatbotId, ...filters }),
    select: (res) => res.data,
    enabled: !!chatbotId,
    ...options,
  });

export const useCreateOffer = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => HRMService.createOffer({ ...data, chatbot_id: chatbotId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hrm-offers', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['hrm-applicants', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['recruitment-dashboard', chatbotId] });
    },
  });
};

export const useUpdateOffer = (chatbotId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) => HRMService.updateOffer(id, { ...data, chatbot_id: chatbotId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hrm-offers', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['hrm-applicants', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['recruitment-dashboard', chatbotId] });
    },
  });
};

// ==================== Recruitment Dashboard Hook ====================

export const useRecruitmentDashboard = (chatbotId, options = {}) =>
  useQuery({
    queryKey: ['recruitment-dashboard', chatbotId],
    queryFn: () => HRMService.getRecruitmentDashboard({ chatbot_id: chatbotId }),
    select: (res) => res.data,
    enabled: !!chatbotId,
    ...options,
  });
